/**
 * Save Form Data Action
 *
 * Synchronous action that merges event.data into context.
 * This is an ACTION (not an actor) - fire-and-forget, no async.
 */

import { assign } from "xstate";

/**
 * Action metadata
 */
export interface ActionMetadata {
  displayName: string;
  description: string;
  category: "context" | "logging" | "analytics" | "spawn" | "navigation";
}

/**
 * Action with metadata wrapper
 */
export interface ActionWithMetadata {
  impl: ReturnType<typeof assign>;
  metadata: ActionMetadata;
}

/**
 * Save Form Data Action
 *
 * Merges event.data into machine context.
 * Used on form submission transitions to persist form values.
 *
 * Example:
 * ```json
 * {
 *   "event": "SUBMIT",
 *   "actions": [{ "type": "saveFormData" }],
 *   "target": "nextState"
 * }
 * ```
 */
export const saveFormData: ActionWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
  impl: assign(({ context, event }: any) => {
    const data = event.data || {};

    // Deep merge insurances to preserve verification data (status, ehrInsuranceId, etc.)
    // when form data only updates basic fields (memberId, payer, state)
    if (data.insurances && context.insurances) {
      return {
        ...data,
        insurances: {
          primaryInsurance: data.insurances.primaryInsurance
            ? {
                ...context.insurances.primaryInsurance,
                ...data.insurances.primaryInsurance,
              }
            : context.insurances.primaryInsurance,
          secondaryInsurance: data.insurances.secondaryInsurance
            ? {
                ...context.insurances.secondaryInsurance,
                ...data.insurances.secondaryInsurance,
              }
            : context.insurances.secondaryInsurance,
        },
      };
    }

    return data;
  }),
  metadata: {
    displayName: "saveFormData",
    description: "Save form data to context",
    category: "context",
  },
};
